import dayjs, { Dayjs } from "dayjs";
import { IWeek } from "lobbie";
import { useCallback, useState } from "react";
import { logDev } from "src/utils";

export const useCalendarMonthYear = (
    minDate?: Dayjs,
): {
    month: number;
    year: number;
    setMonth: (newMonth: number) => void;
    setYear: (newYear: number) => void;
    addMonth: () => Dayjs;
    addYear: () => Dayjs;
    subtractMonth: () => Dayjs | undefined;
    subtractYear: () => Dayjs;
    setPresentMonthAndYear: () => void;
} => {
    const [month, setMonth] = useState<number>(dayjs().month());
    const [year, setYear] = useState<number>(dayjs().year());

    const addYear = useCallback((): Dayjs => {
        setYear(year + 1);
        return dayjs().set("year", year + 1);
    }, [year, setYear]);

    const subtractYear = useCallback((): Dayjs => {
        setYear(year - 1);
        return dayjs().set("year", year - 1);
    }, [year, setYear]);

    const addMonth = useCallback((): Dayjs => {
        const nextMonth = month === 11 ? 0 : month + 1;
        if (month === 11) {
            addYear();
            setMonth(nextMonth);
            return dayjs()
                .set("month", nextMonth)
                .set("year", year + 1);
        } else {
            setMonth(nextMonth);
            return dayjs().set("month", nextMonth).set("year", year);
        }
    }, [month, year, addYear, setMonth]);

    const subtractMonth = useCallback((): Dayjs | undefined => {
        const nextMonth = month === 0 ? 11 : month - 1;
        logDev("calendar.subtractMonth - month", month);
        logDev("calendar.subtractMonth - nextMonth", nextMonth);
        if (month === 0) {
            const newMonthDate = dayjs()
                .set("month", nextMonth)
                .set("year", year - 1);
            if (minDate && newMonthDate.add(1, "month").isBefore(minDate)) return;

            logDev("calendar.subtractMonth - subtracting year");
            subtractYear();
            setMonth(nextMonth);
            return newMonthDate;
        } else {
            const newMonthDate = dayjs().set("month", nextMonth).set("year", year);
            if (minDate && newMonthDate.isBefore(minDate)) return;

            setMonth(nextMonth);
            return newMonthDate;
        }
    }, [month, year, subtractYear, setMonth, minDate]);

    const setPresentMonthAndYear = useCallback(() => {
        const now = dayjs();
        setMonth(now.month());
        setYear(now.year());
    }, [setMonth, setYear]);

    return {
        month,
        year,
        setMonth,
        setYear,
        addMonth,
        addYear,
        subtractMonth,
        subtractYear,
        setPresentMonthAndYear,
    };
};

export const useCalendar = (): [IWeek[], (month: number, year: number) => void, boolean] => {
    const [calendar, setCalendar] = useState<IWeek[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);

    const decemberEdgeCase = useCallback(
        (firstDayOfMonth: Dayjs, cal: { [key: number]: Dayjs[] }) => {
            // annoying december
            if (firstDayOfMonth.month() === 11 && cal[1]) {
                cal[53] = cal[1];
                delete cal[1];
                return cal;
            }
            return cal;
        },
        [],
    );

    const buildCalendar = useCallback(
        (month: number, year: number) => {
            setLoading(true);
            const getNewDayjs = () => dayjs().date(1).month(month).year(year);

            const firstDayOfMonth = getNewDayjs();
            logDev("useCalendar.buildCalendar days in month - ", firstDayOfMonth.daysInMonth());

            // day of the week, 0-6
            const dayPadding = firstDayOfMonth.day();
            const days = Array(firstDayOfMonth.daysInMonth())
                .fill(0)
                .map((_, index: number) =>
                    getNewDayjs()
                        .date(index + 1)
                        .month(month)
                        .year(year),
                );

            const _cal = days.reduce((sum: Record<number, Dayjs[]>, day: Dayjs, index: number) => {
                const week = day.week(); // last week of december is week 1, addressed in decemberEdgeCase

                // pad the beginning of the first week with empty days from previous month
                if (index === 0) {
                    sum[week] = new Array(dayPadding).fill(null).concat(day);
                } else if (sum[week]) {
                    sum[week].push(day);
                } else {
                    sum[week] = [day];
                }
                return sum;
            }, {});

            const cal = decemberEdgeCase(firstDayOfMonth, _cal);
            const _calendar = Object.keys(cal).map((week: string) => ({
                week: Number(week),
                days: cal[Number(week)],
            }));

            setLoading(false);
            setCalendar(_calendar);
        },
        [decemberEdgeCase],
    );

    return [calendar, buildCalendar, isLoading];
};
